import { Box, Grid2, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FeestageLevelStyles, HeaderStyles, TotalCostStyles, CategoryLevelStyles, SubCategoryLevelStyles, NoDataMatchStylesAFP } from '../../assets/styles/CommonStyles';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AFPTrackerFilters, Category, FeeStage, ProposalAfpTracker, ProposalAfpTrackerResponse, Subcategory } from '../../interfaces/AFPTracker.interface';
import { CustomBackdrop, CustomButton, CustomTextField, formatCurrency, SearchBar, Toaster, useApi, useParams } from '../../utils/globalImports';
import { FilterListOffOutlined, SearchOutlined } from '@mui/icons-material';

// ─── Fixed column labels & widths — mirrors InvoiceTracker exactly ────────────
const FixedColumns = ["Code", "Description", "Cost", "Qty", "Total Cost"];
const CustomWidth   = [180, 300, 120, 100, 150];

// ─── Sticky helper — identical signature to InvoiceTracker ───────────────────
const getStickyStyle = (index: number, bgColor: string = "#f3f3f3") => ({
    position: "sticky" as const,
    left: CustomWidth.slice(0, index).reduce((a, b) => a + b, 0),
    zIndex: 1,
    minWidth: CustomWidth[index],
    width: CustomWidth[index],
    backgroundColor: bgColor,
});

// ─── AFP helper — fee-stage level only ───────────────────────────────────────


const AFPTrackerPage: React.FC = () => {

    const [filters, setFilters] = useState<AFPTrackerFilters>({
        searchText: "",
        afpNumber: null,
    });
    const { execute, loading } = useApi();
    const [openProposal,  setOpenProposal]  = useState<Record<number, boolean>>({});
    const [openCategory,  setOpenCategory]  = useState<Record<number, boolean>>({});
    const [openSub,       setOpenSub]       = useState<Record<number, boolean>>({});
    const [data,          setData]          = useState<ProposalAfpTracker[]>([]);
    const [toast,         setToast]         = useState({ open: false, message: "", type: "success" });
    const [TriggerClear,  setTriggerClear]  = useState(false);

    const { projectId } = useParams<{ projectId?: string }>();

    // ── AFP column map (dynamic) ──────────────────────────────────────────────
    const afpNames = useMemo(() => {
        const map = new Map<number, string>();
        data?.forEach((p) =>
            p.categories?.forEach((c) =>
                c.subcategories?.forEach((s) =>
                    s.feeStages?.forEach((f) =>
                        f.afpTracker?.forEach((a) => {
                            if (a.afpNumber !== undefined && a.afpName)
                                map.set(a.afpNumber, a.afpName);
                        })
                    )
                )
            )
        );
        return new Map([...map.entries()].sort((a, b) => a[0] - b[0]));
    }, [data]);

// AFP helper
const getFeeStageAfpValue = (
    fee: FeeStage,
    afpNumber: number
): number =>
    fee?.afpTracker?.find(a => a.afpNumber === afpNumber)?.afpValue ?? 0;

// AFP totals
const getAfpTotals = (feeStages: FeeStage[]) => {
    const afpMap: Record<number, number> = {};

    afpNames.forEach((_, afpNumber) => {
        afpMap[afpNumber] = feeStages.reduce(
            (sum, fee) => sum + getFeeStageAfpValue(fee, afpNumber),
            0
        );
    });

    return afpMap;
};

// Sub Category Totals
const getSubCategoryTotals = (sub: Subcategory) => {
    const cost = sub.feeStages.reduce(
        (sum, stage) => sum + Number(stage.cost || 0),
        0
    );

    const totalCost = sub.feeStages.reduce(
        (sum, stage) => sum + Number(stage.totalCost || 0),
        0
    );

    const months = sub.feeStages.reduce(
        (sum, stage) => sum + Number(stage.quantity || 0),
        0
    );

    const afp = getAfpTotals(sub.feeStages);

    return { cost, totalCost, months, afp };
};

// Category Totals
const getCategoryTotals = (category: Category) => {
    const allStages: FeeStage[] = [];

    category.subcategories.forEach(sub => {
        allStages.push(...(sub.feeStages || []));
    });

    const cost = allStages.reduce(
        (sum, stage) => sum + Number(stage.cost || 0),
        0
    );

    const totalCost = allStages.reduce(
        (sum, stage) => sum + Number(stage.totalCost || 0),
        0
    );

    const months = allStages.reduce(
        (sum, stage) => sum + Number(stage.quantity || 0),
        0
    );

    const afp = getAfpTotals(allStages);

    return { cost, totalCost, months, afp };
};

// Proposal Totals
const getProposalTotals = (proposal: ProposalAfpTracker) => {
    const allStages: FeeStage[] = [];

    proposal.categories.forEach(category => {
        category.subcategories.forEach(sub => {
            allStages.push(...(sub.feeStages || []));
        });
    });

    const cost = allStages.reduce(
        (sum, stage) => sum + Number(stage.cost || 0),
        0
    );

    const totalCost = allStages.reduce(
        (sum, stage) => sum + Number(stage.totalCost || 0),
        0
    );

    const months = allStages.reduce(
        (sum, stage) => sum + Number(stage.quantity || 0),
        0
    );

    const afp = getAfpTotals(allStages);

    return { cost, totalCost, months, afp };
};

// Grand Totals
const grandTotals = useMemo(() => {
    const allStages: FeeStage[] = [];

    data.forEach(proposal => {
        proposal.categories.forEach(category => {
            category.subcategories.forEach(sub => {
                allStages.push(...(sub.feeStages || []));
            });
        });
    });

    const cost = allStages.reduce(
        (sum, stage) => sum + Number(stage.cost || 0),
        0
    );

    const totalCost = allStages.reduce(
        (sum, stage) => sum + Number(stage.totalCost || 0),
        0
    );

    const months = allStages.reduce(
        (sum, stage) => sum + Number(stage.quantity || 0),
        0
    );

    const afp: Record<number, number> = {};

    afpNames.forEach((_, afpNumber) => {
        afp[afpNumber] = allStages.reduce(
            (sum, fee) => sum + getFeeStageAfpValue(fee, afpNumber),
            0
        );
    });

    return { cost, totalCost, months, afp };
}, [data, afpNames]);

    // ── API ───────────────────────────────────────────────────────────────────
    const handleSearch       = () => fetchAFPTracker();
    const handleClearFilters = () => {
        const clearFilters={
            searchText: "", afpNumber: null 
        }
        setFilters(clearFilters);
        setTriggerClear(true);
        fetchAFPTracker(clearFilters);
    };

    useEffect(() => { fetchAFPTracker(); }, []);

    const fetchAFPTracker = async (currentFilters = filters) => {
            
        try {
            const response = await execute<ProposalAfpTrackerResponse>(
                "post",
                `/Project/AFPValues?ProjectID=${Number(projectId)}`,
                { searchText: currentFilters.searchText, afpNumber: currentFilters.afpNumber }
            );
            setData(Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (ex) {
            const error = ex as any;
            setData([]);
            setToast({
                open:    true,
                message: error?.response?.data?.message || error?.message || "Failed to Load Data",
                type:    "error",
            });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Filters panel ──────────────────────────────────────────── */}
            <Paper elevation={0} sx={{ border: "1px solid #e0eaf6", borderRadius: "14px", overflow: "hidden",flexShrink:0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, backgroundColor: "#f0f6ff", borderBottom: "1px solid #e0eaf6" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FilterListOffOutlined sx={{ fontSize: 18, color: "#1e66ab" }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1e66ab" }}>Filters</Typography>
                    </Box>
                </Box>
                <Box sx={{ p: 3, backgroundColor: "#f7faff" }}>
                    <Grid2 container spacing={2} alignItems="flex-end">
                        <Box>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#888", textTransform: "uppercase", mb: 1.5 }}>
                                Search & Filters
                            </Typography>
                            <Grid2 container spacing={2} alignItems="center" wrap="nowrap">
                                <Grid2 size={{ xs: 12, md: 8 }}>
                                    <SearchBar
                                        placeholder="Search By Proposal Number and Proposal Name"
                                        onQueryChange={(value) => setFilters((prev) => ({ ...prev, searchText: value }))}
                                        sx={{ fontSize: "14px" }}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                                        isClear={TriggerClear}
                                        setIsClear={setTriggerClear}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <CustomTextField
                                        name="AFPNumber" label="AFP Number" type="number" hideLabel={true}
                                        value={filters.afpNumber ?? ""}
                                        onChange={(e) => setFilters((prev) => ({
                                            ...prev,
                                            afpNumber: e.target.value ? Number(e.target.value) : null,
                                        }))}
                                    />
                                </Grid2>
                                <Grid2 sx={{ ml: "auto" }}>
                                    <CustomButton capsule sx={{ textTransform: "none", height: "45px", px: 3, whiteSpace: "nowrap" }} onClick={handleSearch} backgroundColor="#1e66ab">
                                        <SearchOutlined sx={{ fontSize: 18, mr: 0.5 }} />Search
                                    </CustomButton>
                                </Grid2>
                                <Grid2 sx={{ ml: "auto" }}>
                                    <CustomButton capsule sx={{ textTransform: "none", height: "45px", p: 2, whiteSpace: "nowrap" }} onClick={handleClearFilters} backgroundColor="#92d050">
                                        Clear All
                                    </CustomButton>
                                </Grid2>
                            </Grid2>
                        </Box>
                    </Grid2>
                </Box>
            </Paper>

            {/* ── Table ──────────────────────────────────────────────────── */}
            <TableContainer sx={{ overflow: "auto", minHeight: 0, mt: 2 }}>
                <Table stickyHeader>

                    {/* ── Header ── */}
                    <TableHead>
                        <TableRow>
                            {/* First 5 sticky fixed columns */}
                            {FixedColumns.map((col, i) => (
                                <TableCell
                                    key={col}
                                    sx={{
                                        ...getStickyStyle(i, "#92d050"),
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        border: "1px solid rgba(0,0,0,0.04)",
                                        zIndex: 10,
                                    }}
                                >
                                    {col}
                                </TableCell>
                            ))}
                            {/* Dynamic AFP columns */}
                            {Array.from(afpNames).map(([afpNumber, afpName]) => (
                                <TableCell key={afpNumber} sx={{ ...HeaderStyles, minWidth: 150, textAlign: "left" }}>
                                    {afpName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* ── Body ── */}
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={FixedColumns.length + afpNames.size} align="center" sx={NoDataMatchStylesAFP}>
                                    No data matching the search criteria
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {data.map((proposal) => {
                                    const pt = getProposalTotals(proposal);
                                    return (
                                        <React.Fragment key={proposal.proposalID}>

                                            {/* ── PROPOSAL ROW ── */}
                                            <TableRow>
                                                <TableCell sx={{ ...getStickyStyle(0) }}>
                                                    <IconButton size="small" onClick={() => setOpenProposal((p) => ({ ...p, [proposal.proposalID]: !p[proposal.proposalID] }))}>
                                                        {openProposal[proposal.proposalID] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ ...getStickyStyle(1), fontWeight: 700, color: "#1e66ab", fontSize: 18 }}>
                                                    {proposal.proposalNumber}
                                                </TableCell>
                                                <TableCell sx={{ ...getStickyStyle(2), fontWeight: 700, color: "#1e66ab", fontSize: 18 }}>
                                                    {formatCurrency(pt.cost)}
                                                </TableCell>
                                                <TableCell sx={{ ...getStickyStyle(3) }} />
                                                <TableCell sx={{ ...getStickyStyle(4), fontWeight: 700, color: "#1e66ab", fontSize: 18 }}>
                                                    {formatCurrency(pt.totalCost)}
                                                </TableCell>
                                                {Array.from(afpNames).map(([afpNumber]) => (
                                                    <TableCell key={afpNumber} sx={{ backgroundColor: "#f3f3f3", fontWeight: 700, color: "#1e66ab", fontSize: 18 }}>
                                                        {formatCurrency(pt.afp[afpNumber])}
                                                    </TableCell>
                                                ))}
                                            </TableRow>

                                            {/* ── CATEGORY ROWS ── */}
                                            {openProposal[proposal.proposalID] &&
                                                proposal.categories.map((category) => {
                                                    const ct = getCategoryTotals(category);
                                                    return (
                                                        <React.Fragment key={category.categoryID}>
                                                            <TableRow>
                                                                <TableCell sx={{ ...getStickyStyle(0) }} />
                                                                <TableCell sx={{ ...CategoryLevelStyles, ...getStickyStyle(1) }}>
                                                                    <IconButton size="small" onClick={() => setOpenCategory((c) => ({ ...c, [category.categoryID]: !c[category.categoryID] }))}>
                                                                        {openCategory[category.categoryID] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                                    </IconButton>
                                                                    {category.categoryName}
                                                                </TableCell>
                                                                <TableCell sx={{ ...getStickyStyle(2), ...CategoryLevelStyles }}>
                                                                    {formatCurrency(ct.cost)}
                                                                </TableCell>
                                                                <TableCell sx={{ ...getStickyStyle(3), ...CategoryLevelStyles }} />
                                                                <TableCell sx={{ ...getStickyStyle(4), ...CategoryLevelStyles }}>
                                                                    {formatCurrency(ct.totalCost)}
                                                                </TableCell>
                                                                {Array.from(afpNames).map(([afpNumber]) => (
                                                                    <TableCell key={afpNumber} sx={{ ...CategoryLevelStyles }}>
                                                                        {formatCurrency(ct.afp[afpNumber])}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>

                                                            {/* ── SUBCATEGORY ROWS ── */}
                                                            {openCategory[category.categoryID] &&
                                                                category.subcategories.map((sub) => {
                                                                    const st = getSubCategoryTotals(sub);
                                                                    return (
                                                                        <React.Fragment key={sub.subcategoryID}>
                                                                            <TableRow>
                                                                                <TableCell sx={{ ...getStickyStyle(0) }} />
                                                                                <TableCell sx={{ ...SubCategoryLevelStyles, ...getStickyStyle(1) }}>
                                                                                    <IconButton size="small" onClick={() => setOpenSub((s) => ({ ...s, [sub.subcategoryID]: !s[sub.subcategoryID] }))}>
                                                                                        {openSub[sub.subcategoryID] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                                                    </IconButton>
                                                                                    {sub.subcategoryName}
                                                                                </TableCell>
                                                                                <TableCell sx={{ ...getStickyStyle(2), ...SubCategoryLevelStyles }}>
                                                                                    {formatCurrency(st.cost)}
                                                                                </TableCell>
                                                                                <TableCell sx={{ ...getStickyStyle(3) }} />
                                                                                <TableCell sx={{ ...getStickyStyle(4), ...SubCategoryLevelStyles }}>
                                                                                    {formatCurrency(st.totalCost)}
                                                                                </TableCell>
                                                                                {Array.from(afpNames).map(([afpNumber]) => (
                                                                                    <TableCell key={afpNumber} sx={{ ...SubCategoryLevelStyles }}>
                                                                                        {formatCurrency(st.afp[afpNumber])}
                                                                                    </TableCell>
                                                                                ))}
                                                                            </TableRow>

                                                                            {/* ── FEE STAGE ROWS ── */}
                                                                            {openSub[sub.subcategoryID] &&
                                                                                sub.feeStages.map((fee) => (
                                                                                    <TableRow key={fee.feeStageID}>
                                                                                        <TableCell sx={{ ...FeestageLevelStyles, ...getStickyStyle(0) }}>
                                                                                            {fee.feeStageReferenceNumber}
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...FeestageLevelStyles, ...getStickyStyle(1) }}>
                                                                                            {fee.description}
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...FeestageLevelStyles, ...getStickyStyle(2) }}>
                                                                                            {formatCurrency(fee.cost)}
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...FeestageLevelStyles, ...getStickyStyle(3) }}>
                                                                                            {fee.quantity}
                                                                                        </TableCell>
                                                                                        <TableCell sx={{ ...FeestageLevelStyles, ...getStickyStyle(4) }}>
                                                                                            {formatCurrency(fee.totalCost)}
                                                                                        </TableCell>
                                                                                        {Array.from(afpNames).map(([afpNumber]) => (
                                                                                            <TableCell key={afpNumber} sx={{ ...FeestageLevelStyles }}>
                                                                                                {formatCurrency(getFeeStageAfpValue(fee, afpNumber))}
                                                                                            </TableCell>
                                                                                        ))}
                                                                                    </TableRow>
                                                                                ))}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                        </React.Fragment>
                                    );
                                })}

                                {/* ── GRAND TOTAL ROW ── */}
                                <TableRow sx={TotalCostStyles}>
                                    <TableCell sx={{ ...getStickyStyle(0), ...TotalCostStyles,position:"sticky",bottom:0 }} />
                                    <TableCell sx={{ ...getStickyStyle(1), ...TotalCostStyles,position:"sticky",bottom:0  }}>Total</TableCell>
                                    <TableCell sx={{ ...getStickyStyle(2), ...TotalCostStyles,position:"sticky",bottom:0  }}>{formatCurrency(grandTotals.cost)}</TableCell>
                                    <TableCell sx={{ ...getStickyStyle(3),position:"sticky",bottom:0  }} />
                                    <TableCell sx={{ ...getStickyStyle(4), ...TotalCostStyles,position:"sticky",bottom:0  }}>{formatCurrency(grandTotals.totalCost)}</TableCell>
                                    {Array.from(afpNames).map(([afpNumber]) => (
                                        <TableCell key={afpNumber} sx={{...TotalCostStyles,position:"sticky",bottom:0 }}>
                                            {formatCurrency(grandTotals.afp[afpNumber])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <CustomBackdrop open={loading} />
            <Toaster
                open={toast.open}
                message={toast.message}
                type={toast.type as "success" | "error"}
                onClose={() => setToast({ ...toast, open: false })}
            />
        </>
    );
};

export default AFPTrackerPage;

